import { cn } from '../../lib/utils';

/**
 * Skeleton — the one placeholder shape for every list/grid scaffold in
 * the app. Replaces the hand-rolled `bg-muted animate-pulse` /
 * `bg-gray-100 animate-pulse` variants scattered across Discover,
 * Collections, and PublicPortfolio, which drifted on rounding, opacity,
 * and dark-mode contrast (bg-gray-100 stays white in dark theme; muted
 * doesn't).
 *
 * `SkeletonCard` is the tuned default for a project-card slot; use the
 * primitive `Skeleton` for anything else.
 */

interface SkeletonProps {
  className?: string;
  /** Optional a11y label; defaults to "Loading". */
  label?: string;
}

export function Skeleton({ className, label = 'Loading' }: SkeletonProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label}
      className={cn('animate-pulse rounded bg-muted', className)}
    >
      <span className="sr-only">{label}</span>
    </div>
  );
}

/** Aspect-ratio image slot. Matches Discover / Collections / PublicPortfolio grid tiles. */
export function SkeletonImage({
  className,
  aspect = '4/3',
  radius = 'xl',
}: {
  className?: string;
  aspect?: '4/3' | '3/2' | '16/9' | '1/1';
  radius?: 'md' | 'lg' | 'xl' | '2xl' | 'full';
}) {
  const aspectMap = { '4/3': 'aspect-[4/3]', '3/2': 'aspect-[3/2]', '16/9': 'aspect-[16/9]', '1/1': 'aspect-square' };
  const radiusMap = { md: 'rounded-md', lg: 'rounded-lg', xl: 'rounded-xl', '2xl': 'rounded-2xl', full: 'rounded-full' };
  return <Skeleton className={cn(aspectMap[aspect], radiusMap[radius], className)} />;
}

/** A single line of text. Height + width variants match common line lengths. */
export function SkeletonText({
  className,
  size = 'md',
  width = 'full',
}: {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  width?: 'full' | '3/4' | '1/2' | '1/3';
}) {
  const sizeMap = { sm: 'h-3', md: 'h-3.5', lg: 'h-5' };
  const widthMap = { full: 'w-full', '3/4': 'w-3/4', '1/2': 'w-1/2', '1/3': 'w-1/3' };
  return <Skeleton className={cn(sizeMap[size], widthMap[width], className)} />;
}

/** Full project-card skeleton — image tile + two lines of text. */
export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn('overflow-hidden rounded-2xl border border-border', className)}>
      <SkeletonImage aspect="4/3" radius="md" className="rounded-b-none" />
      <div className="space-y-2 p-3">
        <SkeletonText size="md" />
        <SkeletonText size="sm" width="1/2" />
      </div>
    </div>
  );
}
